<template>
  <div class="canvas-scroll">
    <v-layout row align-center ml-4 wrap v-if="tabCount > 1">
      <v-flex md4 lg3 class="selectRoomDropdown">
        <div>
          <v-select
            v-model="selection"
            :items="tabArray"
            label="Select Side"
            outline
          ></v-select>
        </div>
      </v-flex>
      <v-flex md4 lg6>
        <div class="live-container" style="margin: 0 auto;">
          <div class="live-data">{{ $t("LIVE") }}</div>
          <v-progress-linear
            style="width: 50px"
            :indeterminate="true"
            height="3"
            color="accent"
          ></v-progress-linear>
        </div>
      </v-flex>
    </v-layout>
    <v-container
      fluid
      grid-list-md
      pa-0
      class="live-bg"
      v-else
    >
      <v-layout row align-center justify-center wrap>
        <v-spacer></v-spacer>
        <v-flex xs1>
          <div class="live-container">
            <div class="live-data">{{ $t("LIVE") }}</div>
            <v-progress-linear
              :indeterminate="true"
              height="3"
              color="accent"
            ></v-progress-linear>
          </div>
        </v-flex>
        <v-spacer></v-spacer>
      </v-layout>
    </v-container>
    <v-layout id="tdHover" column>
      <div id="ioTable"></div>
    </v-layout>
    <div id="drawCanvas"></div>
    <div class="legend">
      <div class="normal">
        <span :class="iconClass.success"></span> {{ $t("Closed / On") }}
      </div>
      <div class="fault">
        <span :class="iconClass.failure"></span> {{ $t("Open / Off / Trip") }}
      </div>
      <div class="acknowledge">
        <span class="circle-grey"></span> {{ $t("No Data") }}
      </div>
    </div>
  </div>
</template>

<script>
const moment = require("moment");
import _ from "lodash";
import { mapGetters, mapMutations } from "vuex";
import { mapGettersMixin } from "@/util/mixin/index";
import { WriteTableRow, WriteAnnTableRow } from "@/components/sld/js/DrawTable";
import sldAttr from "@/components/sld/json/attributes";
import SvgPaper from "@/components/sld/js/SvgPaper";
import SldData from "@/services/SLD";

const computedParameterMap = {
  inverter: [["Generation (kWh)", "Generation (kWh)"]],
  "energy meter": [
    ["Daily Export (kWh)", "Daily Export (kWh)"],
    ["Daily Import (kWh)", "Daily Import (kWh)"],
  ],
  mfm: [
    ["Daily Export (kWh)", "Daily Export (kWh)"],
    ["Daily Import (kWh)", "Daily Import (kWh)"],
  ],
};

const dynamicImageDevices = [
  "inverter",
  "breaker",
  "annunciator"
];

const DATA_TTL_MS = 60000;
const DATA_TTL_CHECK_MS = 10000;
const NUMERIC_DISPLAY_VALUE_REGEX = /^[-+]?(?:\d+\.?\d*|\.\d+)(?:e[-+]?\d+)?$/i;
const NO_DATA_PRESERVED_KEYS = new Set([
  "__sldNoData",
  "id",
  "uId",
  "unique_id",
  "device_id",
  "deviceId",
  "block_id",
  "inverter_room",
  "inverter_id",
  "scb_id",
  "zmu_id",
  "mppt_id",
  "feeder_id",
  "ups_id",
  "transformer_id",
  "em_id",
  "room_id",
  "breaker_id",
  "tracker_id",
  "nifps_id",
  "fire_alarm_id",
]);

const toArray = (value) => Array.isArray(value) ? value : value ? [value] : [];

const mqttDeviceIdFields = {
  inverter: ["block_id", "inverter_room", "inverter_id"],
  zone: ["block_id", "inverter_room", "inverter_id"],
  scb: ["block_id", "inverter_room", "inverter_id", "scb_id"],
  zmu: ["block_id", "inverter_room", "inverter_id", "zmu_id"],
  mppt: ["block_id", "inverter_room", "inverter_id"],
  mfm: ["block_id", "feeder_id"],
  ups: ["block_id", "inverter_room", "ups_id"],
  transformer: ["block_id", "inverter_room", "transformer_id"],
  "energy meter": ["block_id", "em_id"],
  "soil sensor": ["block_id", "soil_sensor_id"],
  annunciator: ["block_id", "device_id"],
  relay: ["block_id", "relay_id"],
  breaker: ["block_id", "room_id", "breaker_id"],
  tracker: ["block_id", "tracker_id"],
  nifps: ["block_id", "inverter_room", "nifps_id"],
  "fire alarm": ["block_id", "inverter_room", "fire_alarm_id"],
};

export default {
  mixins: [mapGettersMixin],
  data() {
    return {
      paper: null,
      tabCount: null,
      selection: "",
      imageMap: {},
      sldSym: {},
      hoverData: {},
      svgImage: {},
      tableDataNode: {},
      initialDrawComplete: {},
      liveDeviceData: {},
      dataExpiresAt: {},
      dataTtlInterval: null,
      mqttBound: false,
      mqttMessageHandler: null,
      mqttErrorHandler: null,
      computedDataRefreshInterval: null,
      skipNextSelectionDraw: false,
    };
  },
  computed: {
    ...mapGetters(["getSelectedPlantId", "getActivePlantInfo"]),
    computedSvgImage() {
      const baseUrl = process.env.VUE_APP_BASE_URL || ("http://" + location.hostname + ":3030");
      let svgObj = {};
      
      Object.keys(this.svgImage).forEach((key) => {
        let logo = this.svgImage[key];

        if (!this.isDark) {
          logo = logo.replace("sld_v1.1/", "sld_v1.1_light/");
        }

        const encodedUrl = encodeURIComponent(logo);
        svgObj[key] = `${baseUrl}/api/v2/assets?url=${encodedUrl}`
      });
      
      return svgObj;
    },
    deviceIconMap() {
      if (_.isEmpty(this.computedSvgImage)) {
        return {};
      } else {
        return {
          inverter: this.computedSvgImage["invImageOff"],
          invImageOn: this.computedSvgImage["invImageOff"],
          invImageOn1: this.computedSvgImage["invImageOff1"],
        };
      }
    },
    tabArray() {
      return this.sldSym.tabs || [];
    },
    iconColor() {
      if (
        this.sldSym.iconStatusColor &&
        this.sldSym.iconStatusColor.failure === "green"
      ) {
        return {
          success: "red",
          failure: "green",
        };
      } else {
        return {
          success: "green",
          failure: "red",
        };
      }
    },
    iconClass() {
      return {
        success:
          this.iconColor.success == "green" ? "circle-green" : "circle-red",
        failure:
          this.iconColor.failure == "green" ? "circle-green" : "circle-red",
      };
    },
    availableDeviceTypes() {
      return this.getDeviceTypesForTab(this.selection);
    },
    allAvailableDeviceTypes() {
      const tabs = toArray(this.sldSym.tabs);
      return [
        ...new Set(
          tabs.reduce((deviceTypes, tabName) => {
            return [
              ...deviceTypes,
              ...this.getDeviceTypesForTab(tabName),
            ];
          }, [])
        ),
      ];
    },
    allTableConfigsByDevice() {
      return toArray(this.sldSym.tabs).reduce((configsByDevice, tabName) => {
        const table = this.sldSym[tabName] && this.sldSym[tabName].table
          ? this.sldSym[tabName].table
          : {};

        Object.keys(table).forEach((device) => {
          if (!configsByDevice[device]) configsByDevice[device] = [];
          configsByDevice[device].push(...toArray(table[device]));
        });

        return configsByDevice;
      }, {});
    },
    tableParameterAvailable() {
      if (this.allAvailableDeviceTypes.length) {
        return _.fromPairs(
          this.allAvailableDeviceTypes.map((d) => {
            return [
              d,
              this.getActivePlantInfo([
                "rule",
                "comp_config",
                "sld",
                "options",
                "table",
              ])[d],
            ];
          })
        );
      }
      return {};
    },
    apiParamConfigByDevice() {
      return this.allAvailableDeviceTypes.reduce((configByDevice, device) => {
        const paramConfig = {
          ...(this.tableParameterAvailable[device] || {}),
        };

        (this.allTableConfigsByDevice[device] || []).forEach((tableConfig) => {
          const hoverConfig = this.getHoverConfig(tableConfig);
          if (!hoverConfig) return;

          [hoverConfig.input, hoverConfig.output].forEach((hoverParamMap) => {
            Object.entries(hoverParamMap || {}).forEach(([rawName, displayName]) => {
              if (this.isApiParamName(rawName)) {
                this.addMissingApiParam(paramConfig, rawName, displayName);
              }
            });
          });
        });

        configByDevice[device] = paramConfig;
        return configByDevice;
      }, {});
    },
    configuredDeviceIdsByDevice() {
      const idsByDevice = {};

      Object.entries(this.allTableConfigsByDevice).forEach(([device, tableConfigs]) => {
        if (!idsByDevice[device]) idsByDevice[device] = new Set();
        tableConfigs.forEach((tableConfig) => {
          Object.keys(tableConfig.data || {}).forEach((id) =>
            idsByDevice[device].add(String(id))
          );
        });
      });

      toArray(this.sldSym.tabs).forEach((tabName) => {
        toArray(this.sldSym[tabName] && this.sldSym[tabName].image)
          .filter((image) => image.device && image.uId)
          .forEach((image) => {
            if (!idsByDevice[image.device]) idsByDevice[image.device] = new Set();
            idsByDevice[image.device].add(String(image.uId));
          });
      });

      return idsByDevice;
    },
    noDataParameterKeysByDevice() {
      return this.allAvailableDeviceTypes.reduce((keysByDevice, device) => {
        const keys = new Set();

        Object.entries(this.getApiParamConfig(device)).forEach(([rawName, displayName]) => {
          keys.add(rawName);
          keys.add(displayName);
        });

        this.getComputedParameterMap(device).forEach(([rawName, displayName]) => {
          keys.add(rawName);
          keys.add(displayName);
        });

        this.getAllTableConfigs(device).forEach((tableConfig) => {
          Object.entries(tableConfig.paramMap || {}).forEach(([rawName, displayName]) => {
            keys.add(rawName);
            keys.add(displayName);
          });

          const hoverConfig = this.getHoverConfig(tableConfig);
          if (!hoverConfig) return;

          [hoverConfig.input, hoverConfig.output].forEach((hoverParamMap) => {
            Object.entries(hoverParamMap || {}).forEach(([rawName, displayName]) => {
              keys.add(rawName);
              keys.add(displayName);
            });
          });
        });

        keysByDevice[device] = keys;
        return keysByDevice;
      }, {});
    },
    colorCode() {
      return {
        null: "#9E9E9E",
        "No Data": "#9E9E9E",
        0: this.iconColor['failure'] == "green" ? "#3CB371" : "#FF5252",
        1: this.iconColor['success'] == "green" ? "#3CB371" : "#FF5252",
      };
    },
  },
  async mounted() {
    try {
      this.SET_PROGRESS_BAR(true);
      this.svgImage = await SldData.getSVGimage("sld");
      this.sldSym = await SldData.getSymbol(this.getSelectedPlantId);
      this.SET_PROGRESS_BAR(false);
      this.selection = this.sldSym.tabs[0];
      this.tabCount = this.sldSym.tabs.length;
    } catch (err) {
      this.SET_PROGRESS_BAR(false);
    }

    if (this.selection) {
      this.fetchInitialApiData();
      this.startDataTtlCheck();
      this.drawSld();
    }
    if (this.isReload) {
      this.startRealtimeUpdates();
    }
  },
  destroyed() {
    this.stopRealtimeUpdates(this.getSelectedPlantId, true);
    this.stopDataTtlCheck();
    this.resetRenderState();
    this.removeCanvasPaper();
  },
  methods: {
    ...mapMutations(["SET_LAST_CALL_TIME", "SET_PROGRESS_BAR"]),
    fetchInitialApiData() {
      let updateTime = moment().format("MM-DD-YYYY H:mm:ss");
      this.SET_LAST_CALL_TIME(updateTime);

      return Promise.all(
        this.allAvailableDeviceTypes.map((device) => {
          return this.fetchDeviceApiData(device);
        })
      )
    },
    async fetchDeviceApiData(device) {
      const paramConfig = this.getApiParamConfig(device);
      if (!this.getSelectedPlantId || _.isEmpty(paramConfig)) return;

      const param = btoa(JSON.stringify(paramConfig));
      try {
        const message = await SldData.getDataV2(this.getSelectedPlantId, device, param);
        this.updateLiveDeviceData(device, message);
      } catch (e) {
        console.error("Error fetching SLD API data for", device, e);
      }
    },
    computedDataDevices() {
      if (!this.getSelectedPlantId) return [];
      const availableDeviceTypes = new Set(this.allAvailableDeviceTypes);

      return Object.keys(computedParameterMap).filter((device) =>
        availableDeviceTypes.has(device) &&
        this.getComputedParameterMap(device).length > 0
      );
    },
    getDeviceTypesForTab(tabName) {
      const tab = this.sldSym[tabName];
      if (!tab || (!tab.devices && !tab.image)) return [];

      return [
        ...new Set([
          ...toArray(tab.image)
            .filter((el) => el.device && el.device !== "" && el.uId !== "")
            .map((el) => el.device),
          ...toArray(tab.devices),
        ]),
      ];
    },
    getTableConfigs(device, tabName = this.selection) {
      const table = this.sldSym[tabName] &&
        this.sldSym[tabName].table
        ? this.sldSym[tabName].table[device]
        : null;

      return toArray(table);
    },
    getAllTableConfigs(device) {
      return this.allTableConfigsByDevice[device] || [];
    },
    getApiParamConfig(device) {
      return this.apiParamConfigByDevice[device] || {};
    },
    isApiParamName(paramName) {
      return typeof paramName === "string" && /^[a-z][a-z0-9_]*$/.test(paramName);
    },
    addMissingApiParam(paramConfig, rawName, displayName) {
      if (!rawName) return;

      const normalizedDisplayName = displayName || rawName;
      const alreadyPresent = Object.entries(paramConfig).some(
        ([existingRawName, existingDisplayName]) =>
          existingRawName === rawName ||
          existingRawName === normalizedDisplayName ||
          existingDisplayName === rawName ||
          existingDisplayName === normalizedDisplayName
      );

      if (!alreadyPresent) {
        paramConfig[rawName] = normalizedDisplayName;
      }
    },
    getActiveParamMapNames(device) {
      return new Set(
        this.getAllTableConfigs(device).reduce((names, tableConfig) => {
          const paramMap = tableConfig.paramMap || {};
          return [
            ...names,
            ...Object.keys(paramMap),
            ...Object.values(paramMap),
          ];
        }, [])
      );
    },
    getComputedParameterMap(device) {
      const activeParamMapNames = this.getActiveParamMapNames(device);

      return (computedParameterMap[device] || []).filter(([param, name]) =>
        activeParamMapNames.has(param) || activeParamMapNames.has(name)
      );
    },
    async fetchComputedDeviceApiData(device) {
      try {
        const message = await SldData.getComputedDataV2(this.getSelectedPlantId, device);
        this.updateLiveDeviceData(device, message, { updateImages: false, refreshTimestamp: false });
      } catch (e) {
        console.error("Error fetching SLD computed data for", device, e);
      }
    },
    fetchComputedApiData() {
      return Promise.all(
        this.computedDataDevices().map((device) =>
          this.fetchComputedDeviceApiData(device)
        )
      );
    },
    startComputedDataRefresh() {
      this.stopComputedDataRefresh();

      if (!this.computedDataDevices().length) return;

      this.fetchComputedApiData();
      this.computedDataRefreshInterval = setInterval(() => {
        this.fetchComputedApiData();
      }, 60000);
    },
    stopComputedDataRefresh() {
      if (this.computedDataRefreshInterval) {
        clearInterval(this.computedDataRefreshInterval);
        this.computedDataRefreshInterval = null;
      }
    },
    stopRealtimeUpdates(plantId = this.getSelectedPlantId, removeListeners = true) {
      this.stopComputedDataRefresh();
      if (plantId) {
        this.$mqtt.unsubscribe(`live/${plantId}/+`);
      }
      if (removeListeners) {
        this.removeMqttListeners();
      }
    },
    removeMqttListeners() {
      this.removeMqttListener("message", this.mqttMessageHandler);
      this.removeMqttListener("error", this.mqttErrorHandler);
      this.mqttMessageHandler = null;
      this.mqttErrorHandler = null;
      this.mqttBound = false;
    },
    removeMqttListener(eventName, handler) {
      if (!handler || !this.$mqtt) return;

      if (typeof this.$mqtt.off === "function") {
        this.$mqtt.off(eventName, handler);
      } else if (typeof this.$mqtt.removeListener === "function") {
        this.$mqtt.removeListener(eventName, handler);
      }
    },
    startRealtimeUpdates() {
      if (!this.isReload) return;
      this.mqttHandler();
      this.startComputedDataRefresh();
    },
    startDataTtlCheck() {
      this.stopDataTtlCheck();
      this.dataTtlInterval = setInterval(() => {
        this.expireStaleDeviceData();
      }, DATA_TTL_CHECK_MS);
    },
    stopDataTtlCheck() {
      if (this.dataTtlInterval) {
        clearInterval(this.dataTtlInterval);
        this.dataTtlInterval = null;
      }
    },
    resetRenderState() {
      Object.values(this.drawDebouncers || {}).forEach((debouncer) => {
        if (debouncer && typeof debouncer.cancel === "function") {
          debouncer.cancel();
        }
      });
      this.imageMap = {};
      this.tableDataNode = {};
      this.initialDrawComplete = {};
      this.drawDebouncers = {};
      this.pendingDrawOptions = {};
    },
    removeCanvasPaper() {
      if (this.paper) {
        this.paper.remove();
        this.paper = null;
      }

      const drawCanvas = document.getElementById("drawCanvas");
      if (drawCanvas) {
        drawCanvas.innerHTML = "";
      }
    },
    updateLiveDeviceData(device, newDataMap, options = {}) {
      if (!newDataMap || typeof newDataMap !== "object") return;
      const { updateImages = true, refreshTimestamp = true } = options;

      if (!this.liveDeviceData[device]) {
        this.$set(this.liveDeviceData, device, {});
      }
      if (!this.dataExpiresAt[device]) {
        this.$set(this.dataExpiresAt, device, {});
      }

      const updatedIds = Object.keys(newDataMap);
      updatedIds.forEach(idKey => {
        if (!refreshTimestamp && this.liveDeviceData[device][idKey] && this.liveDeviceData[device][idKey].__sldNoData) {
          return;
        }

        const normalizedData = {
          ...this.normalizeDeviceData(device, newDataMap[idKey]),
          __sldNoData: false,
        };

        if (!this.liveDeviceData[device][idKey]) {
           this.$set(this.liveDeviceData[device], idKey, normalizedData);
        } else {
           this.$set(this.liveDeviceData[device], idKey, {
             ...this.liveDeviceData[device][idKey],
             ...normalizedData
           });
        }

        if (refreshTimestamp) {
          this.refreshDeviceDataTtl(device, idKey);
        }
      });

      if (this.availableDeviceTypes.includes(device)) {
        this.refreshVisibleDevice(device, { updateImages });
      }
    },
    expireStaleDeviceData() {
      const now = Date.now();
      const changedDevices = new Set();

      Object.entries(this.dataExpiresAt).forEach(([device, deviceExpiries]) => {
        Object.entries(deviceExpiries || {}).forEach(([idKey, expiresAt]) => {
          if (typeof expiresAt !== "number") return;
          if (now < expiresAt) return;

          this.setDeviceDataToNoData(device, idKey, {}, { redraw: false });
          changedDevices.add(device);
        });
      });

      changedDevices.forEach((device) => {
        if (this.availableDeviceTypes.includes(device)) {
          this.refreshVisibleDevice(device);
        }
      });
    },
    refreshVisibleDevice(device, options = {}) {
      this.refreshVisibleHoverTable(device);
      this.debouncedDrawDeviceData(device, options);
    },
    refreshDeviceDataTtl(device, idKey) {
      if (!this.dataExpiresAt[device]) {
        this.$set(this.dataExpiresAt, device, {});
      }

      this.$set(this.dataExpiresAt[device], idKey, Date.now() + DATA_TTL_MS);
    },
    setDeviceDataToNoData(device, idKey, sourceData = {}, options = {}) {
      const { redraw = true } = options;

      if (!device || !idKey) return;
      if (!this.liveDeviceData[device]) {
        this.$set(this.liveDeviceData, device, {});
      }
      if (!this.dataExpiresAt[device]) {
        this.$set(this.dataExpiresAt, device, {});
      }

      const currentData = this.liveDeviceData[device][idKey] || {};
      const noData = this.buildNoDataDeviceData(device, {
        ...currentData,
        ...sourceData,
        id: idKey,
      });

      this.$set(this.liveDeviceData[device], idKey, noData);
      this.$set(this.dataExpiresAt[device], idKey, null);

      if (redraw) {
        if (this.availableDeviceTypes.includes(device)) {
          this.refreshVisibleDevice(device);
        }
      }
    },
    buildNoDataDeviceData(device, data = {}) {
      const noData = { ...data, __sldNoData: true };

      Object.keys(noData).forEach((key) => {
        if (!NO_DATA_PRESERVED_KEYS.has(key)) {
          noData[key] = null;
        }
      });

      this.getNoDataParameterKeys(device).forEach((key) => {
        if (!NO_DATA_PRESERVED_KEYS.has(key)) {
          noData[key] = null;
        }
      });

      return noData;
    },
    getNoDataParameterKeys(device) {
      return this.noDataParameterKeysByDevice[device] || new Set();
    },
    normalizeDeviceData(device, data) {
      if (!data || typeof data !== "object") return data;

      const normalized = { ...data };
      const tableParameters = this.getApiParamConfig(device);

      Object.entries(tableParameters).forEach(([rawName, displayName]) => {
        const rawValue = normalized[rawName];
        const displayValue = normalized[displayName];

        if (rawValue === undefined && displayValue !== undefined) {
          normalized[rawName] = displayValue;
        }
        if (displayValue === undefined && rawValue !== undefined) {
          normalized[displayName] = rawValue;
        }
      });

      return this.applyDerivedHoverValues(device, normalized);
    },
    getCachedDeviceData(device, uId) {
      const deviceData = this.liveDeviceData[device] || {};
      if (deviceData[uId]) return deviceData[uId];

      const id = String(uId);
      const matchedKey = Object.keys(deviceData).find((key) => {
        const data = deviceData[key];
        return (
          String(key) === id ||
          (data &&
            [data.uId, data.id, data.unique_id, data.device_id, data.deviceId]
              .filter((value) => value !== undefined && value !== null)
              .some((value) => String(value) === id))
        );
      });

      return matchedKey ? deviceData[matchedKey] : null;
    },
    refreshVisibleHoverTable(device) {
      const { device: hoverDevice, id, hoverConfig, tableIsLeft } = this.hoverData || {};
      if (hoverDevice !== device || !id || !hoverConfig) return;

      const tableDateContainer = document.getElementById("tdHover");
      if (!tableDateContainer || tableDateContainer.style.display === "none") return;

      this.displayTable(device, id, hoverConfig, tableIsLeft);
    },
    debouncedDrawDeviceData(device, options = {}) {
      if (!this.drawDebouncers) this.drawDebouncers = {};
      if (!this.pendingDrawOptions) this.pendingDrawOptions = {};

      const existingOptions = this.pendingDrawOptions[device] || {};
      this.pendingDrawOptions[device] = {
        updateImages: existingOptions.updateImages || options.updateImages !== false,
      };

      if (!this.drawDebouncers[device]) {
        this.drawDebouncers[device] = _.debounce(() => {
          const drawOptions = this.pendingDrawOptions[device] || {};
          this.pendingDrawOptions[device] = {};
          this.drawDeviceData(device, drawOptions);
        }, 300);
      }
      this.drawDebouncers[device]();
    },
    drawDeviceData(device, options = {}) {
      const { updateImages = true } = options;
      if (!this.paper) { console.warn('[SLD DRAW] No paper, skipping', device); return; }
      const message = this.liveDeviceData[device] || {};
      const table = this.sldSym[this.selection].table[device];
      if (!table) { console.warn('[SLD DRAW] No table config for', device); return; }

      const imageList = this.getImageList(device);
      const tHeight = 20;

      const tableArr = toArray(table);
      if (updateImages) {
        this.updateImageIcons(device, imageList, message);
      }

      const isInitialDraw = !this.initialDrawComplete[device];

      if (this.tableDataNode[device]) {
        Object.keys(this.tableDataNode[device]).forEach((id) => {
          this.tableDataNode[device][id].forEach((el) => el.remove());
        });
      }
      this.tableDataNode[device] = {};

      tableArr.forEach((tableConfig) => {
        const {
          header = [],
          paramMap = {},
          data = {},
          name = "",
          direction,
          threshold = {},
        } = tableConfig;
        const dataHeaderKey = Object.keys(data).length > 0 ? Object.keys(data)[0] : "";
        const headerExist = !(
          name === "" &&
          dataHeaderKey &&
          data[dataHeaderKey] &&
          data[dataHeaderKey].name === ""
        );

        if (isInitialDraw)
          this.renderHeaders(header, paramMap, headerExist, name, tHeight, isInitialDraw);

        this.renderTableData(
          device,
          data,
          paramMap,
          message,
          headerExist,
          tHeight,
          threshold,
          direction,
          isInitialDraw
        );
      });

      this.$set(this.initialDrawComplete, device, true);
    },
    getConfiguredDeviceIds(device) {
      return this.configuredDeviceIdsByDevice[device] || new Set();
    },
    buildMqttDeviceIdCandidates(device, mqttData) {
      const candidates = [];
      const addCandidate = (value) => {
        if (value !== undefined && value !== null && value !== "") {
          candidates.push(String(value));
        }
      };

      const idFields = mqttDeviceIdFields[device] || [];
      const hasCompositeFields = idFields.length && idFields.every((field) => mqttData[field] !== undefined && mqttData[field] !== null);
      if (hasCompositeFields) {
        addCandidate(idFields.map((field) => mqttData[field]).join("-"));
      }

      if (
        device === "breaker" &&
        mqttData.block_id !== undefined &&
        mqttData.breaker_id !== undefined
      ) {
        if (mqttData.room_id !== undefined && mqttData.room_id !== null) {
          addCandidate([mqttData.block_id, mqttData.room_id, mqttData.breaker_id].join("-"));
        }
        if (mqttData.inverter_room !== undefined && mqttData.inverter_room !== null) {
          addCandidate([mqttData.block_id, mqttData.inverter_room, mqttData.breaker_id].join("-"));
        }
      }

      addCandidate(mqttData.id);
      addCandidate(mqttData.uId);
      addCandidate(mqttData.unique_id);
      addCandidate(mqttData.device_id);
      addCandidate(mqttData.deviceId);

      return [...new Set(candidates)];
    },
    resolveMqttDeviceId(device, mqttData) {
      const candidates = this.buildMqttDeviceIdCandidates(device, mqttData);

      if (!candidates.length) {
        console.warn('[SLD MQTT] No ID candidates found in mqttData:', Object.keys(mqttData));
        return null;
      }

      const configuredIds = this.getConfiguredDeviceIds(device);
      const configuredMatch = candidates.find((candidate) => configuredIds.has(candidate));
      if (configuredMatch) return configuredMatch;

      if (!configuredIds.size) {
        console.warn('[SLD MQTT] No table config found for device:', device);
      }

      if (this.liveDeviceData[device]) {
        const existingKey = Object.keys(this.liveDeviceData[device]).find(key => {
          const stored = this.liveDeviceData[device][key];
          return candidates.some(c =>
            String(stored.unique_id) === c ||
            String(stored.device_id) === c ||
            String(stored.deviceId) === c ||
            String(stored.id) === c
          );
        });
        if (existingKey) return existingKey;
      }

      return candidates[0] || null;
    },
    mqttHandler() {
      this.$mqtt.subscribe(`live/${this.getSelectedPlantId}/+`);
      if (this.mqttBound) return;
      this.mqttBound = true;
      this.mqttMessageHandler = (topic, message) => {
        if (topic.startsWith(`live/${this.getSelectedPlantId}/`)) {
          const splitTopic = topic.split('/');
          const device = splitTopic[splitTopic.length - 1];
          if (!this.allAvailableDeviceTypes.includes(device)) return;

          try {
            const payload = JSON.parse(message.toString());
            const deviceList = payload.deviceList || [payload];
            let newDataMap = {};
            const noDataDevices = new Set();

            deviceList.forEach(mqttData => {
              const deviceId = this.resolveMqttDeviceId(device, mqttData);
              if (!deviceId) return;

              if (Number(mqttData.ignore_status) === 1) {
                this.setDeviceDataToNoData(device, deviceId, mqttData, { redraw: false });
                noDataDevices.add(device);
              } else {
                newDataMap = {
                  ...newDataMap,
                  ...this.transformMqttDataMap(device, deviceId, mqttData),
                };
              }
            });

            if (Object.keys(newDataMap).length > 0) {
              this.updateLiveDeviceData(device, newDataMap);
            }
            noDataDevices.forEach((noDataDevice) => {
              if (this.availableDeviceTypes.includes(noDataDevice)) {
                this.refreshVisibleDevice(noDataDevice);
              }
            });
          } catch (e) {
            console.error('MQTT Parsing error in SLD', e);
          }
        }
      };
      this.mqttErrorHandler = (error) => {
        console.log('ERROR: ', error);
      };
      this.$mqtt.on('message', this.mqttMessageHandler);
      this.$mqtt.on('error', this.mqttErrorHandler);
    },
    transformMqttDataMap(device, deviceId, mqttData) {
      const transformed = this.transformMqttData(device, mqttData);

      if (device === "zone") {
        return this.expandCsvMqttRows(deviceId, transformed, [
          { sourceKeys: ["DC Current", "dc_current"], targetKey: "DC Current" },
        ]);
      }

      if (device === "mppt") {
        return this.expandCsvMqttRows(deviceId, transformed, [
          { sourceKeys: ["Power (kW)", "power"], targetKey: "Power (kW)" },
          { sourceKeys: ["Voltage (V)", "voltage"], targetKey: "Voltage (V)" },
          { sourceKeys: ["Current (A)", "current"], targetKey: "Current (A)" },
        ], "Current (A)");
      }

      if (device === "scb") {
        return {
          [deviceId]: this.applyDerivedHoverValues(device, transformed),
        };
      }

      if (device === "transformer") {
        return {
          [deviceId]: this.applyDerivedHoverValues(device, transformed),
        };
      }

      return { [deviceId]: transformed };
    },
    transformMqttData(device, mqttData) {
      const paramConfig = this.getApiParamConfig(device);
      if (!paramConfig) {
        return mqttData;
      }

      const transformed = { ...mqttData };
      Object.entries(paramConfig).forEach(([rawName, displayName]) => {
        if (rawName in mqttData && !(displayName in mqttData)) {
          transformed[displayName] = mqttData[rawName];
        }
      });

      return transformed;
    },
    applyDerivedHoverValues(device, data) {
      if (!data || typeof data !== "object") return data;

      if (device === "scb") {
        return this.expandCsvMqttColumns(data, [
          { sourceKeys: ["current", "Current (A)"], name: "Channel", unit: "(A)" },
        ]);
      }

      if (device === "transformer") {
        const expanded = this.expandCsvMqttColumns(data, [
          { sourceKeys: ["act_win_temp", "WTI", "WTI 1", "Actual WT (°C)", "Actual WT (Â°C)"], name: "Actual WT", unit: "(Â°C)" },
        ]);
        const secondWindingTemp = this.getFirstDefinedValue(expanded, [
          "Actual WT 2 (°C)",
          "Actual WT 2 (Â°C)",
        ]);

        if (expanded.B1 === undefined && secondWindingTemp !== undefined) {
          expanded.B1 = secondWindingTemp;
        }

        return expanded;
      }

      return data;
    },
    expandCsvMqttColumns(data, csvConfigList) {
      const expanded = { ...data };

      csvConfigList.forEach(({ sourceKeys, name, unit }) => {
        const csvData = this.getFirstDefinedValue(expanded, sourceKeys);
        if (csvData === undefined || csvData === null || csvData === "") return;

        String(csvData).split(",").forEach((value, index) => {
          expanded[`${name} ${index + 1} ${unit}`] = value;
        });
      });

      return expanded;
    },
    expandCsvMqttRows(deviceId, data, csvConfigList, rowSourceKey = null) {
      const rowValues = String(
        this.getFirstDefinedValue(data, [rowSourceKey || csvConfigList[0].targetKey, ...(csvConfigList[0].sourceKeys || [])]) || ""
      ).split(",");

      if (!rowValues.length || rowValues[0] === "") return { [deviceId]: data };

      return rowValues.reduce((rows, value, index) => {
        const rowId = `${deviceId}-${index + 1}`;
        const row = { id: rowId };

        csvConfigList.forEach(({ sourceKeys, targetKey }) => {
          const csvData = this.getFirstDefinedValue(data, [targetKey, ...sourceKeys]);
          const values = csvData === undefined || csvData === null ? [] : String(csvData).split(",");
          row[targetKey] = values[index] === undefined ? values[0] : values[index];
        });

        rows[rowId] = row;
        return rows;
      }, {});
    },
    getFirstDefinedValue(data, keys) {
      const key = keys.find((candidate) => Object.prototype.hasOwnProperty.call(data, candidate));
      return key ? data[key] : undefined;
    },
    getImageList(device) {
      if (dynamicImageDevices.includes(device)) {
        return toArray(this.sldSym[this.selection].image).filter(
          (obj) => obj.device === device
        );
      }
      return [];
    },
    getInitialImageSource(device, id) {
      if (device === "inverter") {
        return this.deviceIconMap[id] || this.computedSvgImage[id];
      }

      if (device === "breaker") {
        const breakerIcons = this.getBreakerIcons(id);
        return breakerIcons[2] || this.computedSvgImage[id];
      }

      if (device && device.includes("annunciator")) {
        return this.computedSvgImage.annunciatorGrey || this.computedSvgImage[id];
      }

      return this.computedSvgImage[id];
    },
    renderHeaders(header, paramMap, headerExist, headerName, tHeight, isInitialDraw) {
      header.forEach(({ x, y, width: cWidth }) => {
        if (headerExist) {
          WriteTableRow(
            "#FF7247",
            x,
            y,
            cWidth,
            tHeight,
            this.paper,
            headerName,
            this.isDark,
            isInitialDraw
          );
        }

        Object.values(paramMap).forEach((name) => {
          y = y + tHeight;
          WriteTableRow(
            this.isDark ? "#FFFFFF" : "#04112F",
            x,
            y,
            cWidth,
            tHeight,
            this.paper,
            name,
            this.isDark,
            isInitialDraw
          );
        });
      });
    },
    updateImageIcons(device, imageList, message, isHover = false) {
      imageList
        .filter(
          (el) =>
            el.uId &&
            (isHover
              ? device === el.device && el.uId === this.hoverData.id
              : true)
        )
        .forEach((el) => {
          let idKey = el.uId;
          let jusVal = message[idKey];

          if (
            !(
              this.imageMap[device] &&
              this.imageMap[device][idKey] &&
              this.imageMap[device][idKey].obj
            )
          )
            return;

          if (device === "inverter") {
            this.updateInverterIcon(el, jusVal, idKey);
          } else if (device === "breaker") {
            this.updateBreakerIcon(
              el,
              isHover && jusVal
                ? {
                    __sldNoData: jusVal.__sldNoData,
                    ON: jusVal.breaker_on,
                    OFF: jusVal.breaker_off,
                    TRIP: jusVal.breaker_trip,
                  }
                : jusVal,
              idKey
            );
          } else if (device.includes("annunciator")) {
            this.updateAnnunciatorIcon(device, jusVal, idKey);
          }

          if (el.deg > 0) {
            this.imageMap[device][idKey].obj.transform(`r${el.deg}`);
          }
        });
    },
    renderTableData(
      device,
      tableData,
      paramMap,
      message,
      headerExist,
      tHeight,
      threshold,
      direction,
      isInitialDraw
    ) {
      if (_.isEmpty(paramMap)) return;

      if (!this.tableDataNode[device]) this.tableDataNode[device] = {};

      Object.keys(tableData).forEach((idKey, idx) => {
        const jusVal = message[idKey];
        
        if (!this.tableDataNode[device][idKey])
          this.tableDataNode[device][idKey] = [];

        let { x, y, width: cWidth, name } = tableData[idKey];

        if (headerExist && isInitialDraw) {
          WriteTableRow(
            this.isDark ? "#FFFFFF" : "#04112F",
            x,
            y,
            cWidth,
            tHeight,
            this.paper,
            name,
            this.isDark,
            isInitialDraw
          );
        }

        Object.keys(paramMap).forEach((key) => {
          if (!direction || direction === "default") {
            y += tHeight;
          } else {
            x += cWidth;
          }
          let dataValue = this.getDataValue(device, key, jusVal);
          let color = this.getColorForValue(device, key, dataValue, threshold);
          let text =
            device.includes("annunciator") || device == "relay"
              ? WriteAnnTableRow(
                  color,
                  x,
                  y,
                  cWidth,
                  tHeight,
                  this.paper,
                  "",
                  this.isDark,
                  isInitialDraw
                )
              : WriteTableRow(
                  color,
                  x,
                  y,
                  cWidth,
                  tHeight,
                  this.paper,
                  dataValue,
                  this.isDark,
                  isInitialDraw
                );
          this.tableDataNode[device][idKey].push(text);
        });
      });
    },
    getDataValue(device, key, jusVal) {
      if (!jusVal || jusVal[key] === undefined) return "No Data";
      if (!this.isNumericDisplayValue(jusVal[key])) return "No Data";

      if (device.includes("annunciator")) {
        return this.formatDisplayValue(jusVal[key], 0);
      }

      return this.formatDisplayValue(jusVal[key]);
    },
    updateInverterIcon(el, jusVal, idKey) {
      const defaultSuccessIcon = this.computedSvgImage[el.id];
      const defaultFailureIcon = this.deviceIconMap[el.id] || defaultSuccessIcon;
      

      const [successIcon, failureIcon] = this.iconColor.success === "red" 
        ? [defaultFailureIcon, defaultSuccessIcon]
        : [defaultSuccessIcon, defaultFailureIcon];


      const inverterObj = this.imageMap.inverter[idKey] && this.imageMap.inverter[idKey].obj ? this.imageMap.inverter[idKey].obj : null;
      
      if (!inverterObj) return;
      
      if (jusVal && jusVal.__sldNoData) {
        inverterObj.attr({ src: defaultFailureIcon });
        return;
      }

      const hasStatusData = jusVal && (
        jusVal["Inverter Alert"] !== undefined ||
        jusVal["dc_power"] !== undefined
      );
      if (!jusVal) {
        inverterObj.attr({ src: failureIcon });
        return;
      }
      if (!hasStatusData) return;

      const iconToDisplay = jusVal["Inverter Alert"] === 1 || jusVal["dc_power"] <= 0 ? failureIcon : successIcon;
      
      inverterObj.attr({ src: iconToDisplay });
    },
    updateBreakerIcon(el, jusVal, idKey) {
      const state = this.getBreakerState(jusVal);
      if (state === null) return;

      let iconMap = this.getBreakerIcons(el.id);
      this.imageMap.breaker[idKey].obj.attr({ src: iconMap[state] });
    },
    getBreakerState(jusVal) {
      if (!jusVal) return 2;
      if (jusVal.__sldNoData) return 2;

      const on = this.getStatusValue(jusVal, ["ON", "on", "breaker_on"]);
      const off = this.getStatusValue(jusVal, ["OFF", "off", "breaker_off"]);
      const trip = this.getStatusValue(jusVal, ["TRIP", "trip", "breaker_trip"]);

      if (this.isActiveStatus(on)) return 1;
      if (this.isActiveStatus(off) || this.isActiveStatus(trip)) return 0;

      const state = this.getStatusValue(jusVal, ["state", "status"]);
      if (state !== undefined && state !== null) {
        if (typeof state === "string") {
          const normalizedState = state.toLowerCase();
          if (["on", "closed", "close", "1", "true"].includes(normalizedState)) return 1;
          if (["off", "open", "trip", "tripped", "0", "false"].includes(normalizedState)) return 0;
        }
        if (this.isActiveStatus(state)) return 1;
        if (this.isInactiveStatus(state)) return 0;
      }

      return null;
    },
    getStatusValue(data, keys) {
      const key = keys.find((candidate) => Object.prototype.hasOwnProperty.call(data, candidate));
      return key ? data[key] : undefined;
    },
    isActiveStatus(value) {
      if (Array.isArray(value)) return this.isActiveStatus(value[0]);
      return value === 1 || value === true || value === "1" || value === "true";
    },
    isInactiveStatus(value) {
      if (Array.isArray(value)) return this.isInactiveStatus(value[0]);
      return value === 0 || value === false || value === "0" || value === "false";
    },
    getBreakerIcons(id) {
      if (!id || !["vcb", "isolatorVer", "motorizedSwitchVer", "motorizedSwitchHor", 
                  "earthSwitchVer", "earthSwitchHor", "isolator", "rectangle"].includes(id)) {
        return ["", "", ""];
      }

      const isSuccessIconRed = this.iconColor.success === "red";
      const baseUrlLogo = "https://scada4x-assets.s3.ap-south-1.amazonaws.com/sld_v1.1/";
      
      const createBreakerIconSet = (normalPath, redPath, disabledPath = normalPath) => {
        const successPath = isSuccessIconRed ? redPath : normalPath;
        const failurePath = isSuccessIconRed ? normalPath : redPath;
        return [
          `${baseUrlLogo}${failurePath}`,
          `${baseUrlLogo}${successPath}`,
          `${baseUrlLogo}${disabledPath}`,
        ];
      };

      const iconConfig = {
        vcb: createBreakerIconSet("vcb/sld_vcb_green_ta1_ba1.svg", "vcb/sld_vcb_red_ta1_ba1.svg", "vcb/sld_vcb_ta1_ba1_disable.svg"),
        isolatorVer: createBreakerIconSet("isolator/isolatorVer.svg", "isolator/isolatorVerRed.svg"),
        motorizedSwitchVer: createBreakerIconSet("isolator/motorizedSwitchVer.svg", "isolator/motorizedSwitchRedVer.svg"),
        motorizedSwitchHor: createBreakerIconSet("isolator/motorizedSwitchHor.svg", "isolator/motorizedSwitchRedHor.svg"),
        earthSwitchVer: createBreakerIconSet("isolator/earthSwitchVer.svg", "isolator/earthSwitchRedVer.svg"),
        earthSwitchHor: createBreakerIconSet("isolator/earthSwitchHor.svg", "isolator/earthSwitchRedHor.svg"),
        isolator: createBreakerIconSet("isolator/isolator.svg", "isolator/isolatorRed.svg", "isolator/isolatorDisable.svg"),
        rectangle: createBreakerIconSet("scb/rectangleGreen.svg", "scb/rectangleRed.svg", "scb/rectangleDisable.svg")
      };

      const baseUrl = process.env.VUE_APP_BASE_URL || ("http://" + location.hostname + ":3030");

      return iconConfig[id].map(path => {
        const encodedUrl = encodeURIComponent(path);
        return `${baseUrl}/api/v2/assets?url=${encodedUrl}`;
      });
    },
    updateAnnunciatorIcon(device, jusVal, idKey) {
      const statusTags = Object.values(this.tableParameterAvailable[device] || {});
      const annunciatorObj = this.imageMap[device][idKey].obj;

      if (!jusVal || jusVal.__sldNoData) {
        annunciatorObj.attr({ src: this.computedSvgImage.annunciatorGrey });
        return;
      }

      const hasStatusData = statusTags.some((tag) =>
        Object.prototype.hasOwnProperty.call(jusVal, tag)
      );
      if (!hasStatusData) return;

      let computedData = statusTags.reduce((sum, tag) => sum + (Number(jusVal[tag]) || 0), 0);
      let icon =
        computedData > 0
          ? this.computedSvgImage.annunciatorRed
          : computedData === 0
          ? this.computedSvgImage.annunciator
          : this.computedSvgImage.annunciatorGrey;
      annunciatorObj.attr({ src: icon });
    },
    getColorForValue(device, key, dataValue, threshold) {
      const RED = "#FF5252";
      const BLUE = "#668cf6";
      
      if (dataValue === "No Data") {
        return device.includes("annunciator") || device.includes("relay") 
          ? this.colorCode['No Data'] 
          : RED;
      }
      
      if (device.includes("annunciator") || device.includes("relay")) {
        return parseFloat(dataValue) > 0 ? this.colorCode[1] : this.colorCode[0];
      }
      
      return parseFloat(dataValue) > (threshold[key] || 0) ? BLUE : RED;
    },  
    updateTableData() {
      let device = this.hoverData.device;
      let idKey = this.hoverData.id || "";
      let hoverData = this.hoverData.data || {};
      const table = this.sldSym[this.selection].table[device];
      if (!table) return;
      if (idKey.length === 0) return;

      const imageList = this.getImageList(device);
      const tHeight = 20;
      const tableArr = toArray(table);

      this.updateImageIcons(
        device,
        imageList,
        { [idKey]: hoverData },
        true
      );

      if (this.tableDataNode[device] && this.tableDataNode[device][idKey]) {
        this.tableDataNode[device][idKey].forEach((el) => el.remove());
      }
      if (!this.tableDataNode[device]) this.tableDataNode[device] = {};
      if (!this.tableDataNode[device][idKey]) this.tableDataNode[device][idKey] = [];

      let newVal = {};
      const tableParameters = this.getApiParamConfig(device);
      let paramTable = [
        ...Object.entries(tableParameters),
        ...this.getComputedParameterMap(device),
      ];
      paramTable.forEach(([p1, p2]) => {
        newVal[p2] =
          device === "zmu" || hoverData[p1] === undefined
            ? hoverData[p2]
            : hoverData[p1];
      });

      tableArr.forEach((tableConfig) => {
        const {
          paramMap = {},
          data = {},
          direction,
          threshold = {},
        } = tableConfig;

        if (_.isEmpty(paramMap)) return;
        if (!Object.keys(data).length) return;

        let { x, y, width: cWidth } = data[idKey] || {};
        Object.keys(paramMap).forEach((key) => {
          if (!direction || direction === "default") {
            y += tHeight;
          } else {
            x += cWidth;
          }
          let dataValue = this.getDataValue(device, key, newVal);
          let color = this.getColorForValue(device, key, dataValue, threshold);
          let text =
            device.includes("annunciator") || device == "relay"
              ? WriteAnnTableRow(
                  color,
                  x,
                  y,
                  cWidth,
                  tHeight,
                  this.paper,
                  dataValue,
                  this.isDark,
                  false
                )
              : WriteTableRow(
                  color,
                  x,
                  y,
                  cWidth,
                  tHeight,
                  this.paper,
                  dataValue,
                  this.isDark,
                  false
                );
          this.tableDataNode[device][idKey].push(text);
        });
      });
    },
    setToFixed(value, valueID, precession = 2) {
      if (value === null || value === undefined) {
        return value;
      } else if (valueID === undefined) {
        return value;
      } else {
        return this.formatDisplayValue(value[valueID], precession);
      }
    },
    isNumericDisplayValue(value) {
      if (typeof value === "number") return Number.isFinite(value);
      if (typeof value !== "string") return false;

      const trimmedValue = value.trim();
      if (!trimmedValue) return false;

      return NUMERIC_DISPLAY_VALUE_REGEX.test(trimmedValue);
    },
    formatDisplayValue(value, precision = 2) {
      if (!this.isNumericDisplayValue(value)) return value;

      const formattedValue = Number(value).toFixed(precision);
      if (precision <= 0) return formattedValue;

      return formattedValue
        .replace(/(\.\d*?[1-9])0+$/, "$1")
        .replace(/\.0+$/, "");
    },
    tablePosition: (event) => {
      return event.clientX > screen.width - 550;
    },
    displayTable(device, uId, hoverConfig, tableIsLeft) {
      if (!device || !uId) return;

      const deviceData = this.getCachedDeviceData(device, uId) || {};

      const tableResponse = { [uId]: deviceData };

      const requiredResponse = this.getRequiredResponse(
        device,
        tableResponse[uId]
      );
      this.updateHoverTableData(device, uId, requiredResponse, hoverConfig, tableIsLeft);
      this.updateHoverTableLayout(tableIsLeft);
      this.renderHoverTable(device, hoverConfig, tableResponse);
    },
    getRequiredResponse(device, response) {
      let requiredResponse = {};
      this.getComputedParameterMap(device).forEach(([param]) => {
        requiredResponse[param] = response[param];
      });

      if (device === "inverter") {
        response["alert"] = response["ac_power"] > 0 ? 0 : 1;
      }

      const tableParameters = this.getApiParamConfig(device);
      Object.keys(tableParameters).forEach((el) => {
        requiredResponse[el] = response && response[el] != null ? response[el] : null;
      });
      return requiredResponse;
    },
    updateHoverTableData(device, id, requiredResponse, hoverConfig, tableIsLeft) {
      this.hoverData = {
        device,
        id,
        data: requiredResponse,
        hoverConfig,
        tableIsLeft,
      };

      this.updateTableData();
    },
    updateHoverTableLayout(tableIsLeft) {
      let tableDateContainer = document.getElementById("tdHover");
      tableDateContainer.classList.toggle("rightData", !tableIsLeft);
      tableDateContainer.classList.toggle("leftData", tableIsLeft);
    },
    renderHoverTable(device, hoverConfig, tableResponse) {
      const tableDate = document.getElementById("ioTable");
      const emValue = Object.values(tableResponse)[0];
      let { input: symInput = {}, output: symOutput = {} } = hoverConfig || {};
      let inputKeys = Object.keys(symInput);
      let outputKeys = Object.keys(symOutput);

      let tempTable = `<table id="hoverTable"><tr id="tableRow">`;
      if (inputKeys.length)
        tempTable += `<th class="col">${this.$t(
          "Input"
        )}</th><th class="col">${this.$t("Value")}</th>`;
      if (outputKeys.length)
        tempTable += `<th class="col">${this.$t(
          "Output"
        )}</th><th class="col">${this.$t("Value")}</th>`;
      tempTable += `</tr>`;

      let range = Math.max(inputKeys.length, outputKeys.length);
      for (let index = 0; index < range; index++) {
        let inputIndex = this.setToFixed(
          emValue,
          inputKeys[index],
          device.includes("annunciator") ? 0 : undefined
        );
        let outputIndex = this.setToFixed(
          emValue,
          outputKeys[index],
          device.includes("annunciator") ? 0 : undefined
        );

        tempTable += `<tr id="tableRow">`;
        tempTable += this.renderHoverTableRow(
          inputKeys,
          index,
          symInput,
          inputIndex
        );
        tempTable += this.renderHoverTableRow(
          outputKeys,
          index,
          symOutput,
          outputIndex
        );
        tempTable += `</tr>`;
      }
      tempTable += `</table>`;
      tableDate.innerHTML = tempTable;
    },
    renderHoverTableRow(keys, index, symMap, value) {
      if (!keys.length) return "";
      let hovData = this.getHoverData(value, index, keys.length);
      let className =
        hovData === "No Data" || hovData == null ? "col-no-data" : "col-value";
      return `<td id="tableData" class="col">${
        index < keys.length ? symMap[keys[index]] : ""
      }</td><td id="tableData" class="${className} value-width-col">${hovData}</td>`;
    },
    hideTable() {
      document.getElementById("tdHover").style.display = "none";
      document.getElementById("ioTable").replaceChildren();
    },
    getHoverData(outputIndex, index, totalInputKey) {
      let result = "";

      if (index < totalInputKey) {
        if (outputIndex == null) {
          result = "No Data";
        } else {
          result = outputIndex;
        }
      }
      return result;
    },
    getHoverConfig(tableConfig) {
      const hoverConfig = tableConfig && tableConfig.config_hover;
      if (!hoverConfig || typeof hoverConfig !== "object") return null;

      const input = hoverConfig.input || {};
      const output = hoverConfig.output || {};

      if (!Object.keys(input).length && !Object.keys(output).length) {
        return null;
      }

      return { input, output };
    },
    drawImage() {
      toArray(this.sldSym[this.selection].image).forEach(
        ({ uId, device, id, x, y, width, height, location, deg = 0 }) => {
          let items = this.paper.image(
            this.getInitialImageSource(device, id),
            x,
            y,
            width,
            height
          );

          if (deg > 0) {
            items.transform(`r${deg}`);
          }

          if (uId && dynamicImageDevices.includes(device)) {
            if (!this.imageMap[device]) this.imageMap[device] = {};
            this.imageMap[device][uId] = { obj: items, id };
          }

          if (this.sldSym[this.selection].hoverProperty[device]) {
            items.node.onmouseenter = (event) =>
              this.handleImageHover(event, device, uId);
            items.node.onmouseleave = () => this.hideTable();
          }

          if (location) {
            items.click(() => this.$router.push(location));
          }
        }
      );
    },
    handleImageHover(event, device, uId) {
      const isLeft = this.tablePosition(event);
      let tableDateContainer = document.getElementById("tdHover");
      tableDateContainer.style.display = "flex";

      const tableConfig = this.sldSym[this.selection].table
        ? this.sldSym[this.selection].table[device]
        : null;

      const configHover = toArray(tableConfig).find((obj) => {
        const hoverConfig = this.getHoverConfig(obj);
        if (!hoverConfig) return false;

        const dataIds = Object.keys(obj.data || {});
        return !dataIds.length || dataIds.some((id) => id == uId);
      });

      if (configHover) {
        this.displayTable(
          device,
          uId,
          this.getHoverConfig(configHover),
          isLeft
        );
      } else {
        this.hideTable();
      }
    },
    drawReact() {
      let sldRect = this.sldSym[this.selection].rect
        ? this.sldSym[this.selection].rect
        : [];
      sldRect.forEach((items) => {
        let rect = this.paper.rect(items.x, items.y, items.width, items.height);
        if (!this.isDark && sldAttr[`${items.attr}Light`]) {
          rect.attr(sldAttr[`${items.attr}Light`]);
        } else {
          rect.attr(sldAttr[items.attr]);
        }
        rect.node.id = rect.id;
      });
    },
    drawText() {
      let sldText = this.sldSym[this.selection].text
        ? this.sldSym[this.selection].text
        : [];
      sldText.forEach((textItems) => {
        let text = this.paper.text(textItems.x, textItems.y, textItems.name);
        if (!this.isDark && sldAttr[`${textItems.attr}Light`]) {
          text.attr(sldAttr[`${textItems.attr}Light`]);
        } else {
          text.attr(sldAttr[textItems.attr]);
        }
      });
    },
    drawPath() {
      let sldPath = this.sldSym[this.selection].path
        ? this.sldSym[this.selection].path
        : [];
      sldPath.forEach((pathItems) => {
        let path = this.paper.path(pathItems.image);
        if (!this.isDark && sldAttr[`${pathItems.attr}Light`]) {
          path.attr(sldAttr[`${pathItems.attr}Light`]);
        } else {
          path.attr(sldAttr[pathItems.attr]);
        }
        path.node.id = pathItems.id;
      });
    },
    drawCachedVisibleDeviceData() {
      this.availableDeviceTypes.forEach((device) => {
        if (this.liveDeviceData[device]) {
          this.drawDeviceData(device);
        }
      });
    },
    drawSld() {
      const selectedTab = this.sldSym[this.selection];
      if (!selectedTab || !selectedTab.paper) return;

      this.resetRenderState();
      this.removeCanvasPaper();
      this.paper = new SvgPaper(
        "drawCanvas",
        selectedTab.paper.width,
        selectedTab.paper.height
      );
      
      this.drawText();
      this.drawReact();
      this.drawPath();
      this.drawImage();
      this.drawCachedVisibleDeviceData();
    },
  },
  watch: {
    isReload() {
      if (this.isReload) {
        this.startRealtimeUpdates();
      } else {
        this.stopRealtimeUpdates();
      }
    },
    theme() {
      this.initialDrawComplete = {};
      this.removeCanvasPaper();
      this.drawSld();
    },
    selection(newVal, oldVal) {
      if (this.skipNextSelectionDraw) {
        this.skipNextSelectionDraw = false;
        return;
      }

      if (oldVal != "") {
        this.initialDrawComplete = {};
        this.removeCanvasPaper();
        this.drawSld();
      }
    },
    async getSelectedPlantId(newVal, oldVal) {
      try {
        this.stopRealtimeUpdates(oldVal);
        this.initialDrawComplete = {};
        this.liveDeviceData = {};
        this.hoverData = {};
        this.sldSym = await SldData.getSymbol(this.getSelectedPlantId);
        const nextSelection = this.sldSym.tabs[0] || "";
        this.skipNextSelectionDraw = nextSelection !== this.selection;
        this.selection = nextSelection;
        this.tabCount = this.sldSym.tabs.length || null;
        this.dataExpiresAt = {};
        this.removeCanvasPaper();
        if (this.tabCount) {
          this.fetchInitialApiData();
          this.startDataTtlCheck();
          this.drawSld();
        }
        if (this.isReload) {
          this.startRealtimeUpdates();
        }
      } catch (err) {
        this.removeCanvasPaper();
      }
    },
  },
};
</script>

<style>
#drawCanvas {
  width: 1500px;
  overflow-x: visible;
  position: relative;
  top: 75px;
}

.v-progress-linear {
  margin: 0;
}

.live-data {
  transform-origin: center;
  animation: flipAni 16s infinite ease-in-out;
}

.canvas-scroll {
  overflow-x: auto;
  overflow-y: hidden;
}

.live-container {
  width: 50px;
  text-align: center;
}

.rightside {
  float: right;
}

div #ioTable {
  right: 8px;
  top: 110px;
}

div #tdHover {
  display: none;
  position: fixed;
  right: 8px;
  top: 110px;
  z-index: 3;
}

#hoverTable {
  font-family: "Trebuchet MS", Arial, Helvetica, sans-serif;
  border-collapse: collapse;
  width: 100%;
}

#hoverTable #tableData {
  border: 1px solid #34495e;
  padding: 2px;
}

#hoverTable #tableRow {
  text-align: center;
  background-color: var(--v-primary-base);
  color: var(--v-primaryText-base);
}

.rightData {
  display: none;
  width: 600px;
  position: fixed;
  right: 8px;
  top: 110px;
  z-index: 3;
}

.leftData {
  display: none;
  width: 600px;
  position: fixed;
  left: 8px;
  top: 110px;
  z-index: 3;
}

.col {
  max-width: 120px;
  width: 100px;
  min-width: 100px;
  text-align: center;
  text-wrap: wrap;
  word-wrap: break-word;
  padding: 6px;
  border: 1px solid #34495e;
}

.col-value {
  color: #668cf6;
}

.col-no-data {
  color: #ff5252;
}

.value-width-col {
  max-width: 160px;
  width: 100px;
  min-width: 100px;
  text-align: center;
  text-wrap: wrap;
  word-wrap: break-word;
  padding: 6px;
  border: 1px solid #34495e;
}

.legend {
  position: absolute;
  top: 15px;
  right: 35px;
  width: 140px;
  color: var(--v-primaryText-base);
  border: 1px solid var(--v-primaryText-base);
  padding: 10px;
}

.circle-green {
  display: inline-block;
  width: 10px;
  height: 10px;
  background: #3cb371;
  border-radius: 50%;
}

.circle-red {
  display: inline-block;
  width: 10px;
  height: 10px;
  background: #ff5252;
  border-radius: 50%;
}

.circle-grey {
  display: inline-block;
  width: 10px;
  height: 10px;
  background: #9e9e9e;
  border-radius: 50%;
}

@keyframes flipAni {
  0% {
    transform: rotateY(0deg);
  }
  50% {
    transform: rotateY(180deg);
  }
  100% {
    transform: rotateY(360deg);
  }
}
</style>
